import { HostError } from './host';
import {
  DATA_RECORD_ERROR_REASONS,
  DATA_RECORD_MAX_BYTES,
  DATA_RECORD_MAX_PER_COLLECTION,
  DATA_RECORD_QUERY_DEFAULT_LIMIT,
  DATA_RECORD_QUERY_MAX_IN_VALUES,
  DATA_RECORD_QUERY_MAX_LIMIT,
  DataDefinition,
  DataRecord,
  DataRecordErrorReason,
  RecordFilter,
  RecordPage,
  RecordSort,
} from './protocol';

/**
 * The mock host's collection store: everything behind the `records.items.*`
 * five, lifted out of mock-host.ts so the multi-domain mock keeps only the
 * wiring. Storage, query planning, schema validation, cursoring and subject
 * isolation all live here.
 *
 * This module is deliberately NOT exported from the package barrel: it is the
 * mock's internals, not a contract an application builds against.
 *
 * Offline parity with the BFF is the whole point: the same refusals, in the same
 * order, over the same seeds the document five read.
 */

/**
 * Base64, hand-rolled rather than `btoa`/`Buffer` because the mock runs in a
 * browser, in Node and in Metro. Shared with mock-host's wallet placeholders,
 * whose values are deliberately not decodable into anything a scheme would accept.
 */
const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function base64(ascii: string): string {
  let out = '';
  for (let index = 0; index < ascii.length; index += 3) {
    const bytes = [ascii.charCodeAt(index), ascii.charCodeAt(index + 1), ascii.charCodeAt(index + 2)];
    const chunk = (bytes[0] << 16) | ((bytes[1] || 0) << 8) | (bytes[2] || 0);
    const characters = [chunk >> 18, (chunk >> 12) & 63, (chunk >> 6) & 63, chunk & 63];
    const kept = ascii.length - index >= 3 ? 4 : ascii.length - index + 1;
    out += characters
      .slice(0, kept)
      .map((value) => BASE64_ALPHABET[value])
      .join('');
    out += '='.repeat(4 - kept);
  }
  return out;
}

/** The inverse of `base64`, for the one thing the mock both writes and reads: a query cursor. */
export function unbase64(encoded: string): string {
  let bits = 0;
  let accumulator = 0;
  let out = '';
  for (const character of encoded.replace(/=+$/, '')) {
    const index = BASE64_ALPHABET.indexOf(character);
    if (index < 0) return '';
    accumulator = (accumulator << 6) | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out += String.fromCharCode((accumulator >> bits) & 0xff);
    }
  }
  return out;
}

/**
 * A refusal the records surface names. The reason travels in `details.reason`
 * and the vocabulary code is whatever DATA_RECORD_ERROR_REASONS maps it to —
 * the same pair the BFF's envelope carries, so an application catches one code
 * on both transports.
 */
export const recordFailure = (
  reason: DataRecordErrorReason,
  message: string,
  details: Record<string, unknown> = {},
): never => {
  throw new HostError(DATA_RECORD_ERROR_REASONS[reason], message, { reason, ...details });
};

/**
 * JSON-schema validation, as much of it as a Data Studio definition can express.
 * Shared with the document five in mock-host, so one seeded schema cannot be
 * enforced two ways.
 */
export const storageSchemaErrors = (
  schema: Record<string, unknown>,
  value: unknown,
  path = '$',
): string[] => {
  const errors: string[] = [];
  if ('const' in schema && value !== schema['const'])
    errors.push(`${path} must equal its declared constant`);
  if (Array.isArray(schema['enum']) && !schema['enum'].some((candidate) => candidate === value)) {
    errors.push(`${path} must be one of the declared enum values`);
  }
  const type = schema['type'];
  const matchesType =
    type === undefined ||
    (type === 'object' && !!value && typeof value === 'object' && !Array.isArray(value)) ||
    (type === 'array' && Array.isArray(value)) ||
    (type === 'integer' && Number.isInteger(value)) ||
    (type === 'number' && typeof value === 'number' && Number.isFinite(value)) ||
    (type === 'string' && typeof value === 'string') ||
    (type === 'boolean' && typeof value === 'boolean') ||
    (type === 'null' && value === null);
  if (!matchesType) return [`${path} must be ${String(type)}`];

  if (type === 'object') {
    const record = value as Record<string, unknown>;
    const properties = (schema['properties'] ?? {}) as Record<string, Record<string, unknown>>;
    for (const required of (schema['required'] ?? []) as string[]) {
      if (!(required in record)) errors.push(`${path}.${required} is required`);
    }
    for (const [key, child] of Object.entries(record)) {
      if (properties[key]) errors.push(...storageSchemaErrors(properties[key], child, `${path}.${key}`));
      else if (schema['additionalProperties'] === false) errors.push(`${path}.${key} is not allowed`);
    }
  }
  if (type === 'array' && schema['items'] && typeof schema['items'] === 'object') {
    (value as unknown[]).forEach((item, index) =>
      errors.push(
        ...storageSchemaErrors(schema['items'] as Record<string, unknown>, item, `${path}[${index}]`),
      ),
    );
  }
  if (type === 'string') {
    if (typeof schema['minLength'] === 'number' && (value as string).length < schema['minLength']) {
      errors.push(`${path} is shorter than minLength`);
    }
    if (typeof schema['maxLength'] === 'number' && (value as string).length > schema['maxLength']) {
      errors.push(`${path} is longer than maxLength`);
    }
    if (typeof schema['pattern'] === 'string' && !new RegExp(schema['pattern']).test(value as string)) {
      errors.push(`${path} does not match its pattern`);
    }
  }
  return errors;
};

/** A path into the author's own JSON ('status', 'customer.id'), never a column of the row. */
export const fieldValue = (record: DataRecord, field: string): unknown =>
  field
    .split('.')
    .reduce<unknown>(
      (current, key) =>
        current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined,
      record.value,
    );

/** Ordering for the range operators and `orderBy`; undefined when the pair is not comparable. */
export const compareValues = (left: unknown, right: unknown): number | undefined => {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  if (typeof left === 'string' && typeof right === 'string') {
    return left < right ? -1 : left > right ? 1 : 0;
  }
  return undefined;
};

export const matchesFilter = (record: DataRecord, filter: RecordFilter): boolean => {
  const actual = fieldValue(record, filter.field);
  if (filter.op === 'eq') return actual === filter.value;
  if (filter.op === 'in') {
    return (filter.value as unknown[]).some((candidate) => candidate === actual);
  }
  const order = compareValues(actual, filter.value);
  if (order === undefined) return false;
  if (filter.op === 'lt') return order < 0;
  if (filter.op === 'lte') return order <= 0;
  if (filter.op === 'gt') return order > 0;
  return order >= 0;
};

export const sortRecords = (rows: DataRecord[], orderBy: RecordSort[]): DataRecord[] =>
  orderBy.length === 0
    ? rows
    : [...rows].sort((left, right) => {
        for (const { field, direction } of orderBy) {
          const order = compareValues(fieldValue(left, field), fieldValue(right, field)) ?? 0;
          if (order !== 0) return direction === 'desc' ? -order : order;
        }
        return 0;
      });

/**
 * The cursor is an offset and nothing else — opaque to the caller, and
 * deliberately not a token: it carries no identity, so it cannot be replayed
 * into anything.
 */
export const encodeCursor = (offset: number): string => base64(`offset:${offset}`);

export const decodeCursor = (cursor: string): number => {
  const match = /^offset:(\d+)$/.exec(unbase64(cursor));
  if (!match) {
    throw new HostError('invalid-payload', 'records.items.query: cursor is not one this host issued');
  }
  return Number(match[1]);
};

/**
 * Everything a query is refused for, checked before a single row is read: a
 * store that answers from declared indexes cannot fall back to a scan, so an
 * unindexed or undeclared field is a refusal rather than a slow answer.
 */
export const assertQuery = (
  declaration: DataDefinition,
  where: RecordFilter[],
  orderBy: RecordSort[],
): void => {
  const declared = Object.keys((declaration.schema?.['properties'] ?? {}) as Record<string, unknown>);
  const indexed = new Set((declaration.indexes ?? []).flatMap(({ fields }) => fields));
  for (const field of [...where.map((filter) => filter.field), ...orderBy.map((sort) => sort.field)]) {
    if (declared.length > 0 && !declared.includes(field.split('.')[0] as string)) {
      recordFailure('query-unknown-field', `${declaration.reference} declares no field '${field}'`, {
        field,
      });
    }
    if (indexed.size > 0 && !indexed.has(field)) {
      recordFailure('query-unindexed-field', `${declaration.reference} has no index covering '${field}'`, {
        field,
      });
    }
  }
  for (const filter of where) {
    if (filter.op !== 'in') continue;
    if (!Array.isArray(filter.value)) {
      throw new HostError('invalid-payload', `'${filter.field}' in-filter needs an array of values`);
    }
    if (filter.value.length > DATA_RECORD_QUERY_MAX_IN_VALUES) {
      recordFailure(
        'query-too-many-values',
        `'${filter.field}' in-filter takes at most ${DATA_RECORD_QUERY_MAX_IN_VALUES} values`,
        { field: filter.field, values: filter.value.length },
      );
    }
  }
  const required = declaration.requiredQueryFilters ?? [];
  if (required.length === 0) return;
  // A range filter does not scope a query, so only eq/in count as satisfying one.
  const scoped = new Set(where.filter(({ op }) => op === 'eq' || op === 'in').map(({ field }) => field));
  if (!required.some((set) => set.every((field) => scoped.has(field)))) {
    recordFailure(
      'query-missing-required-filter',
      `${declaration.reference} requires a filter on one of: ${required
        .map((set) => set.join(' + '))
        .join(', ')}`,
      { requiredQueryFilters: required },
    );
  }
};

/**
 * Whether the current subject may see a row at all. Applied before any `where`
 * clause, because it is the store's scoping, not the caller's filter.
 *
 * A `user`-scoped definition confines every read AND write to the subject's own
 * rows, which is the rule the real service applies; `organisation` scope is
 * visible to everyone in the org and merely records who wrote each row.
 *
 * A seeded row with no `ownerUserId` belongs to whoever is signed in. That is
 * what keeps `{ value }` a complete seed, and it is the only way to seed rows
 * for the current subject without knowing the fixture user's id. Seed an
 * explicit `ownerUserId` to make a row somebody else's.
 */
const visibleTo = (declaration: DataDefinition, row: DataRecord, subject: string | undefined): boolean =>
  declaration.scope !== 'user' || row.ownerUserId === undefined || row.ownerUserId === subject;

export interface MockCollectionRecordSeed extends Partial<DataRecord> {
  value: Record<string, unknown>;
}

export interface MockCollectionsOptions {
  /** The project's Data Studio definitions, documents and collections alike. */
  definitions: DataDefinition[];
  /** Seeded rows keyed by definition (slug or `data/<slug>`). */
  seeds?: Record<string, MockCollectionRecordSeed[]>;
  /**
   * The signed-in subject, read on every call rather than captured once: the
   * mock's session moves under it (login, logout, a completed auth flow).
   */
  subjectOf: () => string | undefined;
  /** The clock the stamps come from. */
  now?: () => string;
}

export interface MockCollections {
  /** The mirror guard: the document five refuse a collection definition. */
  assertDocument(reference: string): void;
  query(reference: string, payload: Record<string, unknown>): RecordPage;
  get(reference: string, payload: Record<string, unknown>): DataRecord | null;
  create(reference: string, payload: Record<string, unknown>): DataRecord;
  update(reference: string, payload: Record<string, unknown>): DataRecord;
  remove(reference: string, payload: Record<string, unknown>): { recordId: string; deleted: boolean };
}

export function createMockCollections({
  definitions,
  seeds = {},
  subjectOf,
  now = () => new Date().toISOString(),
}: MockCollectionsOptions): MockCollections {
  let recordCounter = 0;
  /**
   * Collection rows by definition reference, in insertion order — the whole
   * collection store. A Map of arrays rather than one flat list because every
   * item operation is scoped to one definition and the per-collection cap is too.
   */
  const collectionRecords = new Map<string, DataRecord[]>(
    Object.entries(seeds).map(([seeded, rows]) => {
      const reference = seeded.startsWith('data/') ? seeded : `data/${seeded}`;
      return [
        reference,
        rows.map((row) => {
          const stamp = row.createdAt ?? now();
          return {
            recordId: row.recordId ?? `record-${++recordCounter}`,
            definition: reference,
            revision: row.revision ?? 1,
            value: row.value,
            createdAt: stamp,
            updatedAt: row.updatedAt ?? stamp,
            ...(row.ownerUserId ? { ownerUserId: row.ownerUserId } : {}),
          } satisfies DataRecord;
        }),
      ];
    }),
  );
  /** `<definition>:<subject>:<idempotencyKey>` → the row the first create wrote. */
  const recordIdempotency = new Map<string, DataRecord>();

  const declarationOf = (reference: string): DataDefinition | undefined =>
    definitions.find(({ reference: candidate }) => candidate === reference);

  /**
   * The definition behind an item operation. An ABSENT `shape` is a document
   * (protocol.ts), so a definition seeded without one is 'wrong-shape' here
   * rather than silently becoming a collection because rows were seeded.
   */
  const collectionDefinition = (reference: string): DataDefinition => {
    const declaration = declarationOf(reference);
    if (!declaration) throw new HostError('host-error', `unknown data definition: ${reference}`);
    if (declaration.shape !== 'collection') {
      recordFailure('wrong-shape', `${reference} is a document definition — use the records.* five`);
    }
    return declaration;
  };

  const rowsOf = (reference: string): DataRecord[] => {
    let rows = collectionRecords.get(reference);
    if (!rows) collectionRecords.set(reference, (rows = []));
    return rows;
  };

  /** The rows this subject may address, in store order. */
  const visibleRows = (declaration: DataDefinition, reference: string): DataRecord[] => {
    const subject = subjectOf();
    return rowsOf(reference).filter((row) => visibleTo(declaration, row, subject));
  };

  const assertRecordValue = (declaration: DataDefinition, value: unknown): Record<string, unknown> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      recordFailure('schema-validation', `${declaration.reference} requires a JSON object`);
    }
    const encoded = JSON.stringify(value);
    if (encoded.length > DATA_RECORD_MAX_BYTES) {
      recordFailure(
        'payload-too-large',
        `${declaration.reference} record is larger than ${DATA_RECORD_MAX_BYTES} bytes`,
        { bytes: encoded.length },
      );
    }
    const errors = storageSchemaErrors(declaration.schema, value);
    if (errors.length) {
      recordFailure('schema-validation', `${declaration.reference} failed schema validation`, { errors });
    }
    return value as Record<string, unknown>;
  };

  const assertRecordRevision = (reference: string, ifRevision: unknown, actual: number): void => {
    if (ifRevision === undefined) return;
    if (Number(ifRevision) !== actual) {
      recordFailure('invalid-revision', `${reference} revision changed`, {
        expectedRevision: Number(ifRevision),
        actualRevision: actual,
      });
    }
  };

  return {
    assertDocument(reference) {
      if (declarationOf(reference)?.shape === 'collection') {
        recordFailure('wrong-shape', `${reference} is a collection — use the records.items.* five`);
      }
    },

    query(reference, payload) {
      const declaration = collectionDefinition(reference);
      const where = (payload['where'] as RecordFilter[] | undefined) ?? [];
      const orderBy = (payload['orderBy'] as RecordSort[] | undefined) ?? [];
      assertQuery(declaration, where, orderBy);
      const limit = Math.min(
        Math.max(1, Number(payload['limit'] ?? DATA_RECORD_QUERY_DEFAULT_LIMIT)),
        DATA_RECORD_QUERY_MAX_LIMIT,
      );
      const offset = payload['cursor'] == null ? 0 : decodeCursor(String(payload['cursor']));
      const matched = sortRecords(
        visibleRows(declaration, reference).filter((row) =>
          where.every((filter) => matchesFilter(row, filter)),
        ),
        orderBy,
      );
      return {
        items: matched.slice(offset, offset + limit).map((row) => ({ ...row })),
        nextCursor: offset + limit < matched.length ? encodeCursor(offset + limit) : null,
      } satisfies RecordPage;
    },

    get(reference, payload) {
      const declaration = collectionDefinition(reference);
      const row = visibleRows(declaration, reference).find(
        ({ recordId }) => recordId === String(payload['recordId'] ?? ''),
      );
      return row ? { ...row } : null;
    },

    create(reference, payload) {
      const declaration = collectionDefinition(reference);
      const subject = subjectOf();
      const idempotencyKey =
        payload['idempotencyKey'] === undefined ? undefined : String(payload['idempotencyKey']);
      // The subject is part of the key: one person's replay must never answer
      // with a row written by another.
      const replayKey = `${reference}:${subject ?? ''}:${idempotencyKey}`;
      // A replay answers the row the FIRST create wrote — before validation,
      // because the first call already decided the outcome.
      if (idempotencyKey !== undefined && recordIdempotency.has(replayKey)) {
        return { ...(recordIdempotency.get(replayKey) as DataRecord) };
      }
      const value = assertRecordValue(declaration, payload['value']);
      const rows = rowsOf(reference);
      if (rows.length >= DATA_RECORD_MAX_PER_COLLECTION) {
        recordFailure(
          'collection-full',
          `${reference} holds the maximum ${DATA_RECORD_MAX_PER_COLLECTION} records`,
        );
      }
      const stamp = now();
      const created: DataRecord = {
        recordId: `record-${++recordCounter}`,
        definition: reference,
        revision: 1,
        value,
        createdAt: stamp,
        updatedAt: stamp,
        ...(subject ? { ownerUserId: subject } : {}),
      };
      rows.push(created);
      if (idempotencyKey !== undefined) recordIdempotency.set(replayKey, created);
      return { ...created };
    },

    update(reference, payload) {
      const declaration = collectionDefinition(reference);
      const recordId = String(payload['recordId'] ?? '');
      const rows = rowsOf(reference);
      const subject = subjectOf();
      // Another subject's row is not merely unwritable, it is absent: answering
      // anything else would tell the caller it exists.
      const index = rows.findIndex(
        (row) => row.recordId === recordId && visibleTo(declaration, row, subject),
      );
      if (index < 0) {
        recordFailure('record-not-found', `${reference} holds no record '${recordId}'`);
      }
      const current = rows[index] as DataRecord;
      assertRecordRevision(reference, payload['ifRevision'], current.revision);
      const updated: DataRecord = {
        ...current,
        value: assertRecordValue(declaration, payload['value']),
        revision: current.revision + 1,
        updatedAt: now(),
      };
      rows[index] = updated;
      return { ...updated };
    },

    remove(reference, payload) {
      const declaration = collectionDefinition(reference);
      const recordId = String(payload['recordId'] ?? '');
      const rows = rowsOf(reference);
      const subject = subjectOf();
      const index = rows.findIndex(
        (row) => row.recordId === recordId && visibleTo(declaration, row, subject),
      );
      if (index < 0) {
        // Deleting twice is a no-op that says so; an `ifRevision` against
        // nothing is revision 0, which is a mismatch for any real guard. Another
        // subject's row takes this branch too, so a delete cannot probe for it.
        assertRecordRevision(reference, payload['ifRevision'], 0);
        return { recordId, deleted: false };
      }
      assertRecordRevision(reference, payload['ifRevision'], (rows[index] as DataRecord).revision);
      rows.splice(index, 1);
      return { recordId, deleted: true };
    },
  };
}
